import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'

import { CheckCircleIcon } from 'lucide-react'

import { TooltipPortal } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/utils/cn'

import { getPhaseDescription } from '../../../constants'
import { useAssessmentProcess } from '../../../contexts/AssessmentProcessContext'
import {
  type AssessmentProcessPhase,
  type AssessmentProcessPhaseDescription
} from '../../../types/assessmentProcess.types'

interface NavigationItem {
  path: string
  phaseDescription: AssessmentProcessPhaseDescription
}

export const AssessmentProcessNavigation: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  // const { assessmentProgress, currentPhaseUnsavedChanges } = useAssessmentProcess()
  const { assessmentProgress, incrementPhaseNavigationKey } = useAssessmentProcess()
  const { hasRoles } = useAuth()
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null)
  const navRefs = useRef<Record<string, HTMLAnchorElement | null>>({})
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = (phase: string) => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }

    // Set new timeout to show tooltip after delay
    tooltipTimeoutRef.current = setTimeout(() => {
      setVisibleTooltip(phase)
    }, 500) // 500ms delay
  }

  const handleMouseLeave = () => {
    setVisibleTooltip(null)

    // Clear timeout if mouse leaves before tooltip shows
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }
  }

  const allNavigationItems: NavigationItem[] = [
    {
      path: `/assessments/${id}/dashboard`,
      phaseDescription: getPhaseDescription('dashboard')
    },
    {
      path: `/assessments/${id}/maturity`,
      phaseDescription: getPhaseDescription('maturity')
    },
    {
      path: `/assessments/${id}/gap-analysis`,
      phaseDescription: getPhaseDescription('gap-analysis')
    },
    {
      path: `/assessments/${id}/recommendations`,
      phaseDescription: getPhaseDescription('recommendations')
    },
    {
      path: `/assessments/${id}/planning`,
      phaseDescription: getPhaseDescription('planning')
    },
    {
      path: `/assessments/${id}/reportes`,
      phaseDescription: getPhaseDescription('reportes')
    }
  ]

  // Fases en revisión por cambio de proceso (reingeniería): visibles solo
  // para usuarios con el rol "Beta" (funcionalidades en diseño/prueba).
  const FASES_EN_REVISION: AssessmentProcessPhase[] = ['recommendations', 'planning']

  // La pestaña "Reportes" es visible para quienes ejecutan el assessment
  // (Consultor y superiores) — son quienes generan el Excel de entrega
  // (RPT-DET-01) al cliente, no solo administradores.
  const ROLES_CON_ACCESO_REPORTES = ['Administrador', 'Gerente', 'Consultor']

  const navigationItems = allNavigationItems.filter((item) => {
    if (item.phaseDescription.phase === 'reportes' && !hasRoles(ROLES_CON_ACCESO_REPORTES)) {
      return false
    }
    if (FASES_EN_REVISION.includes(item.phaseDescription.phase) && !hasRoles(['Beta'])) {
      return false
    }
    return true
  })

  const getProgressForPhase = (phase: string) => {
    const progress = assessmentProgress.find((p) => p.phase === phase)
    return progress?.percentage || 0
  }

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex space-x-1 py-2">
          {navigationItems.map((item) => {
            const Icon = item.phaseDescription.icon
            const progress = getProgressForPhase(item.phaseDescription.phase)

            return (
              <div key={item.path} className="flex-1 relative">
                <NavLink
                  ref={(el) => {
                    navRefs.current[item.phaseDescription.phase] = el
                  }}
                  to={item.path}
                  onClick={incrementPhaseNavigationKey}
                  onMouseEnter={() => handleMouseEnter(item.phaseDescription.phase)}
                  onMouseLeave={handleMouseLeave}
                  className={({ isActive }) =>
                    cn(
                      'flex flex-1 items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                      'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
                      'relative group w-full',
                      isActive
                        ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                        : 'text-gray-600 hover:text-gray-900'
                    )
                  }
                >
                  <div className="flex flex-col items-center space-y-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Icon className="h-5 w-5" />
                      <span className="truncate">{item.phaseDescription.title}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="text-gray-500">{item.phaseDescription.subtitle}</span>
                      {/* {progress > 0 && progress < 100 && (
                        <span className="text-primary-600 font-medium">({progress}%)</span>
                      )} */}
                      {progress === 100 && <CheckCircleIcon className="h-4 w-4 text-green-400" />}
                    </div>

                    {/* Progress bar */}
                    {progress > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Unsaved changes indicator */}
                  {/*
                  {currentPhaseUnsavedChanges &&
                    item.phaseDescription.phase === item.phaseDescription.phase && (
                      <div className="absolute top-2 right-2">
                        <div className="h-2 w-2 bg-yellow-400 rounded-full animate-pulse" />
                      </div>
                    )}
                  */}
                </NavLink>

                {/* Tooltip */}
                <TooltipPortal
                  isVisible={visibleTooltip === item.phaseDescription.phase}
                  targetRef={{
                    current: navRefs.current[item.phaseDescription.phase]
                  }}
                  placement="bottom"
                >
                  <div className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 max-w-xs">
                    <p className="font-medium mb-1">{item.phaseDescription.title}</p>
                    <p className="text-gray-300">{item.phaseDescription.description}</p>
                  </div>
                </TooltipPortal>
              </div>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
